import { Row } from "@tanstack/react-table";
import { OneLicense } from "../pages/LicenseKeys/model";
import { EditRowProduct } from "../pages/Products/ProductsSubRow/ProductsSubRow";
import { EditRowUser } from "../pages/Users/UsersSubRow/UsersSubRow";
import { EditRowProject } from "../pages/Projects/ProjectSubRow/ProjectSubRow";

export enum WidthFormControl {
    XS_SMALL = 120,
    X_SMALL = 140,
    SMALL = 180,
    Date = 150,
    NORMAL = 290,
    BIG = 320,
}

export type Feature = { name: string; id: number };
export type FeatureList = { name: string; id: number }[];

export const Roles = {
    ADMIN: "admin",
    USER: "user",
    SUPPORT: "support",
} as const;

export const StateAction = {
    ACTIVATE: "activate",
    DEACTIVATE: "deactivate",
    EXPIRE: "expire",
    RESET: "reset",
    DELETE: "delete",
    GRACE_PERIOD: "grace",
} as const;

export type StateActionPermission =
    (typeof StateAction)[keyof typeof StateAction];

export const AccessRoles = {
    ALL: "all",
    ADMIN: "admin",
    USER: "user",
    SUPPORT: "support",
} as const;

export type AccessPermissionRoles =
    (typeof AccessRoles)[keyof typeof AccessRoles];
export type PermissionRoles = (typeof Roles)[keyof typeof Roles];

export type UserRole = { name: string; id: number };
export type UserRoles = { name: string; id: number }[];

// Granular action permissions carried by the JWT `permission` claim.
// Used for action-level gating (edit/delete/generate/manage) via hasPermission.
// Role-based routing/navigation still uses Roles/AccessRoles.
export const Permissions = {
    LICENSE_READ: "License.Read",
    LICENSE_EDIT: "License.Edit",
    LICENSE_GENERATE: "License.Generate",
    LICENSE_DELETE: "License.Delete",
    PRODUCT_READ: "Product.Read",
    PRODUCT_EDIT: "Product.Edit",
    PRODUCT_DELETE: "Product.Delete",
    PROJECT_READ: "Project.Read",
    PROJECT_EDIT: "Project.Edit",
    PROJECT_DELETE: "Project.Delete",
    USER_MANAGE_ACCESS: "User.ManageAccess",
} as const;

export type Permission = (typeof Permissions)[keyof typeof Permissions];

export type Products = Product[];

export type Product = {
    id: number;
    name: string;
    title: string;
    description: string;
    days: number;
    hasLifeTime: boolean;
    isFree: boolean;
    isTrial: boolean;
    isDisabled: boolean;
    isOpen?: boolean;
    priority: number;
    features: FeatureList;
    projectId: number;
    gracePeriod: number;
    migratedProductsCount?: number;

    totalLicenses: ProductInfo["totalLicenses"]; // Removed after separating ProductInfo --- IGNORE ---
    soldLicenses: ProductInfo["soldLicenses"]; // Removed after separating ProductInfo --- IGNORE ---
    expiredLicenses: ProductInfo["expiredLicenses"]; // Removed after separating ProductInfo --- IGNORE ---
    gracePeriodLicenses: ProductInfo["gracePeriodLicenses"]; // Removed after separating ProductInfo --- IGNORE ---
    unsoldLicenses: ProductInfo["unsoldLicenses"]; // Removed after separating ProductInfo --- IGNORE ---
    activatedLicenses: ProductInfo["activatedLicenses"]; // Removed after separating ProductInfo --- IGNORE ---
};

export type ProductInfo = {
    productName: string;

    totalLicenses: number;
    soldLicenses: number;
    expiredLicenses: number;
    gracePeriodLicenses: number;
    unsoldLicenses: number;
    activatedLicenses: number;
};

export type ProductData = {
    totalProducts: number;
    products: Product[];
    filteredProductsCount: number | undefined;
};

export type User = {
    id: number;
    username: string;
    role: any;
    password?: any;
    isOpen?: boolean;
    permissions?: string[];
    productIds?: number[];
    projectIds?: number[];
};

export type UsersT = User[];

export type ProjectProduct = {
    id?: number;
    name: string;
    status?: number;
}[];

export type Project = {
    id: number;
    name: string;
    features?: FeatureList;
    products?: ProjectProduct;
    isOpen?: boolean;
    migratedProductsCount?: number;
};

export type ProjectData = {
    totalProjects: number;
    projects: Project[];
    filteredProjectsCount: number | undefined;
};

export interface TypeAction {
    type: "update" | "create" | "";
}

export interface ProductTable extends Product {
    subRows?: EditRowProduct;
}

export type ProjectList = Project[];
export interface PropsProject extends ProjectList {}

export interface LicenseTable extends OneLicense {
    subRows?: OneLicense;
}
export interface newRow extends Omit<Product, "id"> {
    isOpen: boolean;
    removed: undefined;
}

export interface UserTable extends User {
    subRows?: EditRowUser;
    subComponentProps?: string[];
}
export interface ProjectsTable extends Project {
    subRows?: EditRowProject;
}

export type renderSubComponentProductTable<P> = {
    (props: {
        row: Row<Product>;
        subRowHandler?: (id: number) => void;
        subComponentProps?: P;
    }): JSX.Element;
};
export type renderSubComponentOneLicenseTable = {
    (props: {
        row: Row<OneLicense>;
        subRowHandler?: (id: number) => void;
    }): JSX.Element;
};

export type renderSubComponentUserTable<P> = {
    (props: {
        row: Row<User>;
        subRowHandler?: (id: number) => void;
        subComponentProps?: P;
    }): JSX.Element;
};
export type renderSubComponentProjectTable = {
    (props: {
        row: Row<Project>;
        subRowHandler?: (id: number) => void;
    }): JSX.Element;
};

export interface CSVUploadData {
    formData: FormData;
    options: Options;
}
export interface Options {
    forceOverride: boolean;
}
